import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArtifactSelectorModal, {
  type Dataset as ArtifactSelectorDataset,
  type EnrichedArtifact,
} from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";
import DatasetSelectorModal from "src/reusable/Chat/PromptInput/DatasetSelectorModal";
import AttachmentsContainer from "src/reusable/Chat/PromptInput/AttachmentsContainer";
import { useFileUploadWithProgress } from "src/reusable/hooks/useFileUploadWithProgress";
import { formatFileSize } from "src/helpers/formatFileSize";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { useVnvAssistStore } from "../store/VnvAssistStore";
import { ACCEPTED_FILE_TYPES } from "src/constants";
import { NucToast } from "src/reusable/components/Toast";
import type { FileUploadState } from "src/reusable/types/fileUploadWithProgress";

/**
 * Right-panel: lets the user upload a new document or pick an existing
 * artifact from a dataset as the VNV input document.
 *
 * File display reuses AttachmentsContainer (same as RFPViewer).
 * Removal from the upload hook is handled correctly in handleRemoveFile.
 */
const VnvAssistInputDocumentPanel = () => {
  const {
    selectedArtifact,
    setSelectedArtifact,
    getOrCreateDialogue,
    dialogueId,
  } = useVnvAssistStore();
  const { config: chatConfig } = useChatStore();
  const { mutateAsync: createDialogue } = useCreateDialogue();

  // Two-step "select from dataset" flow
  const [isSearchDatasetModalOpen, setIsSearchDatasetModalOpen] =
    useState(false);
  const [isArtifactModalOpen, setIsArtifactModalOpen] = useState(false);
  const [inputSearchDatasets, setInputSearchDatasets] = useState<
    ArtifactSelectorDataset[]
  >([]);

  // Create (or reuse) a dialogue so the upload has a context to attach to
  const handleFileSelection = useCallback(async () => {
    try {
      await getOrCreateDialogue(async () => {
        const id = await createDialogue({
          conversation: {
            model_id: chatConfig?.modelId ?? null,
            model_version_number: chatConfig?.modelVersionNum ?? null,
            search_datasets: [],
            n_search_results: chatConfig?.topSearchResults || 5,
            model_inference_data: {
              infer_data: JSON.stringify([]),
              stream: true,
              streaming_response_required: true,
            },
            stream: true,
            tool_calling: false,
            chat_config_id: chatConfig?.chatConfigId || null,
            product: "vnv-assist",
          },
        });
        return id;
      });
    } catch {
      NucToast({
        message: "Failed to prepare upload. Please try again.",
        type: "error",
      });
    }
  }, [chatConfig, createDialogue, getOrCreateDialogue]);

  const {
    files: uploadedFiles,
    removeFile: removeUploadedFile,
    updateContextId,
    isDragActive,
    getRootProps,
    getInputProps,
    openFileDialog,
  } = useFileUploadWithProgress({
    context: "dialogue",
    contextId: "temp",
    maxFiles: 1,
    maxFileSize: Infinity,
    acceptedFileTypes: ACCEPTED_FILE_TYPES.EXTENSIONS.CHAT_PROJECT,
    allowMultiple: false,
    uploadConcurrency: "sequential",
    autoStartUpload: true,
    showFileList: false,
    onFilesSelected: handleFileSelection,
    onError: (_file, error) => {
      NucToast({ message: `Upload error: ${error}`, type: "error" });
    },
  });

  // Sync dialogue ID into upload context whenever one is established
  useEffect(() => {
    if (dialogueId) updateContextId(dialogueId);
  }, [dialogueId, updateContextId]);

  // When the uploaded file finishes processing, set it as selectedArtifact
  const prevArtifactIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (uploadedFiles.length === 0) return;
    const f = uploadedFiles[0];
    if (
      f.processingStatus === "completed" &&
      f.artifactId &&
      Number(f.artifactId) !== prevArtifactIdRef.current
    ) {
      const artifactId = Number(f.artifactId);
      prevArtifactIdRef.current = artifactId;
      setSelectedArtifact({
        id: artifactId,
        filename: f.file.name,
        filetype: f.file.name.split(".").pop() ?? "",
        filesize: f.file.size,
        create_date: new Date().toISOString(),
        create_user_fk: 0,
        dataset_id: 0,
        dataset_name: "",
      });
    }
  }, [uploadedFiles, setSelectedArtifact]);

  // Build the display file for AttachmentsContainer
  const displayFiles = useMemo((): FileUploadState[] => {
    // In-flight or just-uploaded file takes priority
    if (uploadedFiles.length > 0) {
      return [uploadedFiles[0]];
    }
    // Artifact selected from modal
    if (selectedArtifact) {
      const mockFile = new File([], selectedArtifact.filename, {
        type: "application/octet-stream",
      });
      const size = selectedArtifact.filesize ?? 0;
      return [
        {
          file: mockFile,
          id: `artifact-${selectedArtifact.id}`,
          artifactId: selectedArtifact.id,
          uploadStatus: "uploaded",
          uploadProgress: 100,
          processingStatus: "completed",
          processingProgress: 100,
          sizeFormatted: size > 0 ? formatFileSize(size) : undefined,
          isFromDialogueHistory: true,
        },
      ];
    }
    return [];
  }, [uploadedFiles, selectedArtifact]);

  // Handle removal from AttachmentsContainer
  const handleRemoveFile = useCallback(
    (fileId: string) => {
      if (fileId.startsWith("artifact-")) {
        // Modal-selected artifact: just clear the store
        setSelectedArtifact(null);
      } else {
        // Uploaded file: remove from hook AND clear store
        const hookFile = uploadedFiles.find((f) => f.id === fileId);
        if (hookFile) {
          removeUploadedFile(hookFile.id);
        }
        setSelectedArtifact(null);
        prevArtifactIdRef.current = undefined;
      }
    },
    [uploadedFiles, removeUploadedFile, setSelectedArtifact],
  );

  // Two-step modal handlers
  const handleInputSearchDatasetsConfirm = useCallback(
    (datasets: ArtifactSelectorDataset[]) => {
      setInputSearchDatasets(datasets);
      setIsSearchDatasetModalOpen(false);
      setIsArtifactModalOpen(true);
    },
    [],
  );

  const handleArtifactConfirm = useCallback(
    (artifacts: EnrichedArtifact[]) => {
      if (artifacts.length > 0) {
        // Clear any in-flight or just-uploaded file before switching to the
        // modal-picked artifact.
        for (const f of uploadedFiles) {
          removeUploadedFile(f.id);
        }
        prevArtifactIdRef.current = undefined;
        setSelectedArtifact(artifacts[0]);
      }
      setIsArtifactModalOpen(false);
    },
    [uploadedFiles, removeUploadedFile, setSelectedArtifact],
  );

  const hasFile = displayFiles.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Section title */}
      <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}>
        <Typography variant="body1" fontWeight="medium">
          Input Document
        </Typography>
        <Tooltip title="Select or upload the technical document you want to fact-check. The AI will identify factual claims in this document and validate them against your reference datasets.">
          <IconButton size="small" sx={{ p: 0.25 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Upload drop zone — shown only when no file is selected */}
      {!hasFile && (
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "divider",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            backgroundColor: isDragActive
              ? "action.hover"
              : "background.default",
            transition: "all 0.2s",
            cursor: "pointer",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "action.hover",
            },
            mb: 2,
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon
            sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
          />
          <Typography variant="body2">
            {isDragActive
              ? "Drop file here..."
              : "Drag & drop or choose a file"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1.5}
          >
            PDF, DOCX, DOC, TXT
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<CloudUploadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              openFileDialog();
            }}
          >
            Upload File
          </Button>
        </Box>
      )}

      {/* File display — AttachmentsContainer, same as RFPViewer */}
      {hasFile && (
        <Box mb={2}>
          <AttachmentsContainer
            processedFiles={displayFiles}
            removeFile={handleRemoveFile}
            isDragActive={false}
            uploadError={
              displayFiles[0]?.uploadStatus === "failed" ||
              displayFiles[0]?.processingStatus === "failed"
                ? "Upload failed"
                : null
            }
          />
        </Box>
      )}

      {/* Select from dataset button */}
      <Button
        variant="outlined"
        startIcon={<InsertDriveFileIcon />}
        onClick={() => setIsSearchDatasetModalOpen(true)}
        sx={{ textTransform: "none", alignSelf: "flex-start" }}
        size="small"
      >
        {hasFile ? "Change Document" : "Select from Dataset"}
      </Button>

      {/* Step 1: dataset picker */}
      <DatasetSelectorModal
        open={isSearchDatasetModalOpen}
        onClose={() => setIsSearchDatasetModalOpen(false)}
        onConfirm={handleInputSearchDatasetsConfirm}
        defaultSelectedDatasets={inputSearchDatasets}
      />

      {/* Step 2: artifact picker */}
      <ArtifactSelectorModal
        open={isArtifactModalOpen}
        onClose={() => setIsArtifactModalOpen(false)}
        onConfirm={handleArtifactConfirm}
        datasets={inputSearchDatasets}
        defaultSelectedArtifacts={selectedArtifact ? [selectedArtifact] : []}
      />
    </Box>
  );
};

export default VnvAssistInputDocumentPanel;
